import React from 'react'
import { RiEyeLine, RiEyeOffLine } from '@remixicon/react'
import { type VariantProps } from 'tailwind-variants'
import { cx, focusInput, labelClass, hasErrorInput, tv } from '../../lib/utils'

const inputVariants = tv({
  base: [
    'relative block w-full appearance-none rounded-lg border px-3 py-2 shadow-sm outline-none transition sm:text-sm',
    'border-gray-300 dark:border-gray-800',
    'text-gray-900 dark:text-gray-50',
    'placeholder:text-gray-400 dark:placeholder:text-gray-500',
    'bg-white dark:bg-gray-950',
    'disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400',
    'dark:disabled:border-gray-700 dark:disabled:bg-gray-800 dark:disabled:text-gray-500',
    focusInput,
  ],
})

interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  label?: string
  error?: string
  helpText?: string
  enableShowPassword?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      error,
      helpText,
      enableShowPassword,
      disabled,
      id,
      ...props
    },
    forwardedRef,
  ) => {
    const [showPassword, setShowPassword] = React.useState(false)
    const inputId = id || props.name

    const isPassword = type === 'password'
    const inputType = isPassword && showPassword ? 'text' : type

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className={labelClass}
          >
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={forwardedRef}
            id={inputId}
            type={inputType}
            className={cx(
              inputVariants(),
              error && hasErrorInput,
              isPassword && enableShowPassword && 'pr-10',
              className,
            )}
            disabled={disabled}
            {...props}
          />
          {isPassword && enableShowPassword && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <RiEyeOffLine className="size-4" aria-hidden="true" />
              ) : (
                <RiEyeLine className="size-4" aria-hidden="true" />
              )}
            </button>
          )}
        </div>
        {helpText && !error && (
          <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
            {helpText}
          </p>
        )}
        {error && (
          <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'

// Textarea component
const textareaVariants = tv({
  base: [
    'relative block w-full appearance-none rounded-lg border px-3 py-2 shadow-sm outline-none transition sm:text-sm',
    'border-gray-300 dark:border-gray-800',
    'text-gray-900 dark:text-gray-50',
    'placeholder:text-gray-400 dark:placeholder:text-gray-500',
    'bg-white dark:bg-gray-950',
    'disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400',
    'dark:disabled:border-gray-700 dark:disabled:bg-gray-800 dark:disabled:text-gray-500',
    'read-only:bg-gray-50 read-only:text-gray-600 dark:read-only:bg-gray-900 dark:read-only:text-gray-300',
    focusInput,
  ],
})

interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  helpText?: string
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, helpText, disabled, id, ...props }, forwardedRef) => {
    const inputId = id || props.name

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className={labelClass}
          >
            {label}
          </label>
        )}
        <textarea
          ref={forwardedRef}
          id={inputId}
          className={cx(textareaVariants(), error && hasErrorInput, className)}
          disabled={disabled}
          {...props}
        />
        {helpText && !error && (
          <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
            {helpText}
          </p>
        )}
        {error && (
          <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    )
  },
)

Textarea.displayName = 'Textarea'

// Select component
const selectVariants = tv({
  base: [
    'relative block w-full appearance-none rounded-lg border px-3 py-2 shadow-sm outline-none transition sm:text-sm',
    'border-gray-300 dark:border-gray-800',
    'text-gray-900 dark:text-gray-50',
    'bg-white dark:bg-gray-950',
    'disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400',
    'dark:disabled:border-gray-700 dark:disabled:bg-gray-800 dark:disabled:text-gray-500',
    focusInput,
  ],
})

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  options: { value: string; label: string }[]
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, options, disabled, id, ...props }, forwardedRef) => {
    const inputId = id || props.name

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className={labelClass}
          >
            {label}
          </label>
        )}
        <select
          ref={forwardedRef}
          id={inputId}
          className={cx(selectVariants(), error && hasErrorInput, className)}
          disabled={disabled}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && (
          <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    )
  },
)

Select.displayName = 'Select'

export { Input, Textarea, Select, inputVariants, textareaVariants, selectVariants }
export default Input
